import { Spin, Button } from 'antd'
import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Actions from '../../../actions'
import { clinicTypes, planTypes } from '../../../consts'
import $t from '../../../i18n'
import hipaaIcon from '../../../resources/hipaaIcon.png'
import secureIcon from '../../../resources/secureIcon.png'
import { getIsHmoMember, isMemberTermed } from '../../../utils/hmoUtils'
import {
  filterMHPendingAppointments,
  filterPrimaryPendingAppointments,
} from '../../../utils/appointmentsUtils'
import {
  isNotAPrimarySubscription,
  isBlackListedStatePlanMH,
} from '../../../utils/paymentUtils'
import validateZipcode from '../../../utils/validateZipcode'
import Dialog from '../../common/Dialog'
import Page from '../../common/Page'
import PaymentPlanCard from '../../common/PaymentPlanCard'
import DeclareLocationDialog from '../DeclareLocation'

import './PaymentPlans.css'

const PaymentPlans = () => {
  const dispatch = useDispatch()
  // const plans = useSelector(state => state.payment.plans)
  const allPlans = useSelector(state => state.payment.plans)
  const { subscriptionsLoading, shouldShowNoSubscriptionsModal } = useSelector(
    state => state.payment
  )
  const { zipcode } = useSelector(state => state.auth.locationVerification)
  const selectedService = useSelector(state => state.getCare.selectedService)
  const ipLocationState = useSelector(state => state.app.locationState)
  const isFromCA = ipLocationState === 'CA'
  const userState = validateZipcode(zipcode)
  const userDetails = useSelector(state => state?.profile?.userDetails)
  const isHmoMember = getIsHmoMember(userDetails)
  const isPendingPrimary = useSelector(
    state =>
      filterPrimaryPendingAppointments(state.appointments?.appointments)
        .length > 0
  )
  const isPendingMH = useSelector(
    state =>
      filterMHPendingAppointments(state.appointments?.appointments).length > 0
  )

  // filter plans of we have selected service primary (after primary bot)
  const plans = isPendingPrimary
    ? allPlans?.filter(
        plan => !(plan?.clinic && plan?.type === planTypes.ONE_TIME)
      )
    : allPlans

  useEffect(() => {
    dispatch(Actions.createAction(Actions.ENABLE_FROM_PAYMENT_PLANS))
    dispatch(Actions.createAction(Actions.SUBSCRIPTION_PLANS_REQUESTED))
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        eventName: 'screen_loaded_web',
        data: { screen: 'PlansScreen' },
      })
    )
    return () => {
      dispatch(Actions.createAction(Actions.DISABLE_FROM_PLANS_PAGE))
    }
  }, [])

  const renderPaymentMentalPlansCards = mhPlans => {
    if (isHmoMember || isMemberTermed(userDetails)) {
      return mhPlans
        .filter(plan => plan.type === planTypes.ONE_TIME)
        .map(eachPlan => {
          return (
            <PaymentPlanCard key={eachPlan.id.toString()} plan={eachPlan} />
          )
        })
    } else {
      return mhPlans.map(eachPlan => {
        return <PaymentPlanCard key={eachPlan.id.toString()} plan={eachPlan} />
      })
    }
  }
  const renderPaymentPrimaryPlansCards = () => {
    if (plans && plans.length > 0) {
      return plans
        .filter(plan => {
          if (isFromCA) return isNotAPrimarySubscription(plan)
          // To show one time as clinic null & other subscription with clinic but not the mental
          if (isHmoMember || isMemberTermed(userDetails)) {
            return (
              (plan.clinic === null || plan.clinic) &&
              plan.clinic != clinicTypes.MENTAL &&
              plan.type &&
              plan.type === planTypes.ONE_TIME
            )
          } else {
            return (
              (plan.clinic === null || plan.clinic) &&
              plan.clinic != clinicTypes.MENTAL
            )
          }
        })
        .map(eachPlan => {
          return (
            <PaymentPlanCard key={eachPlan.id.toString()} plan={eachPlan} />
          )
        })
    }
  }

  const renderIcons = () => (
    <div className="payment-icons-container">
      <img className="hipaa-icon" src={hipaaIcon} alt="hipaa icon" />
      <img src={secureIcon} alt="secure icon" />
    </div>
  )

  const renderMhPlans = () => {
    if (plans && plans.length > 0) {
      const mhPlans = plans.filter(plan => {
        if (isFromCA) return plan?.type === planTypes.MEDICAL_FILE
        if (userState) return isBlackListedStatePlanMH(plan, userState)
        return plan.clinic === clinicTypes.MENTAL
      })
      if (mhPlans.length) {
        return (
          <Page
            className="payment-cards-container"
            title={$t('pages.getStarted.paymentPlans.mentalHealthPlans')}
            subtitle={$t('pages.getStarted.paymentPlans.subscriptionOption')}
          >
            {renderPaymentMentalPlansCards(mhPlans)}
          </Page>
        )
      }
    }
  }

  const renderPrimaryPlans = () => {
    return (
      <Page
        className="payment-cards-container"
        title={$t('pages.getStarted.paymentPlans.primaryCarePlans')}
        subtitle={$t('pages.getStarted.paymentPlans.subscriptionOption')}
      >
        {renderPaymentPrimaryPlansCards()}
      </Page>
    )
  }

  const showPrimaryPlans = () => {
    return selectedService !== clinicTypes.MENTAL && !isPendingMH
      ? renderPrimaryPlans()
      : null
  }

  const showMhPlans = () => {
    return selectedService && selectedService !== clinicTypes.MENTAL
      ? null
      : renderMhPlans()
  }
  const renderNoSubscriptionsModal = () => {
    const onClose = () => {
      dispatch(Actions.createAction(Actions.CLOSE_NO_SUBSCRIPTIONS_MODAL))
    }

    return (
      <Dialog
        isVisible={shouldShowNoSubscriptionsModal}
        title={$t('dialogs.locationVerify.noSubscriptionsMsg', {
          state: userState ? userState : $t('dialogs.locationVerify.yourState'),
        })}
        footerActions={[
          <Button type="primary" onClick={onClose}>
            {$t('common.gotIt')}
          </Button>,
        ]}
        onCancel={onClose}
      ></Dialog>
    )
  }

  return (
    <Page className="payment-plans" grey>
      {!subscriptionsLoading ? (
        <div className="payment-plans-container">
          <div className="payment-plans-cards">
            {showPrimaryPlans()}
            {showMhPlans()}
          </div>
          {renderIcons()}
          <DeclareLocationDialog isDialog />
          {renderNoSubscriptionsModal()}
        </div>
      ) : (
        <Spin size="large"></Spin>
      )}
    </Page>
  )
}

export default PaymentPlans
